import { FunctionComponent, useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, List, ListItem, ListItemText, Paper, ClickAwayListener } from '@mui/material';
import DashboardLayout from '../../../components/layout/layout';
import { useNavigate } from 'react-router-dom';
import SearchInventoryIcon from '../../../assets/img/inventory_black.png';
import { StyledTextField } from '../../../components/ui/formInput';
import { useGetInventoryAutocomplete } from '../../../hooks/api/user';
//import RecentInventorySearch from '../../../components/common/recent-inventory-search';

type ValidateField = {
  invalid: boolean;
  error: string;
};

type ValidateFieldsState = {
  partNumber: ValidateField;
};

const LeadTime: FunctionComponent = () => {
  const navigate = useNavigate();

  const [partNumber, setPartNumber] = useState<string>('');
  const [showAutocomplete, setShowAutocomplete] = useState<boolean>(false);
  const [debouncedPrefix, setDebouncedPrefix] = useState<string>('');
  const autocompleteRef = useRef<HTMLDivElement>(null);
  
  const formValidation: ValidateFieldsState = {
    partNumber: { invalid: false, error: '' }
  };
  const [validateFields, setValidateFields] = useState<ValidateFieldsState>(formValidation);

  // Autocomplete query
  const { data: autocompleteData, isLoading: isAutocompleteLoading } = useGetInventoryAutocomplete(debouncedPrefix);
  const suggestions = autocompleteData?.partNumbers || [];

  const validateForm = () => {
    let valid = true;
    const errors = { ...validateFields };

    if (!partNumber?.trim()) {
      errors.partNumber = { invalid: true, error: 'Please enter a part number.' };
      valid = false;
    } else {
      errors.partNumber = { invalid: false, error: '' };
    }
    setValidateFields(errors);
    return valid;
  };

  // Debounce effect for autocomplete
  useEffect(() => {
    const timer = setTimeout(() => {
      if (partNumber.trim().length >= 2) {
        setDebouncedPrefix(partNumber.trim());
        setShowAutocomplete(true);
      } else {
        setShowAutocomplete(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [partNumber]);

  // Handle suggestion selection - auto-search
  const handleSuggestionClick = (suggestion: string) => {
    setPartNumber(suggestion);
    setShowAutocomplete(false);
    // Auto-trigger search
    setTimeout(() => {
      navigate(`/inventory/lead-time/search-result?partNo=${encodeURIComponent(suggestion)}`);
    }, 100);
  };

  // Handle input change
  const handlePartNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPartNumber(value);
    setValidateFields(formValidation); // Clear validation errors
  };

  // Handle click away from autocomplete
  const handleClickAway = () => {
    setShowAutocomplete(false);
  };

  const handleAction = (e?: React.FormEvent<HTMLFormElement>) => {
    if (e) e.preventDefault();
    setValidateFields(formValidation);
    if (!validateForm()) {
      return;
    }
    const trimmed = partNumber.trim();
    navigate(`/inventory/lead-time/search-result?partNo=${encodeURIComponent(trimmed)}`);
  }

  return (
    <>
      <DashboardLayout titleIcon={<img src={SearchInventoryIcon} alt="Search Inventory" style={{ width: 25, height: 25 }} />} title={"Inventory"} initialBreadcrumbs={"Channel Partners/ Inventory"} levelBreadcrumbs={''} breadcrumbs={' List Price Lead Time'} activeMenu={'inventory'}>
        <Box sx={{ width: '100%', maxWidth: '100%' }}>
          <Box
            sx={{
              padding: 1,
              mt: 1,
            }}
          >
            <Typography variant="subtitle1" color="#000000" gutterBottom fontWeight="bold" sx={{ fontSize: 16 }}>
              {/* sx={{ mb: 1, fontSize: 14 }} */}
            Barksdale Part Number:
          </Typography>

          <form onSubmit={handleAction}>
            <Box sx={{ display: 'flex', gap: 2, position: 'relative' }}>
              <Box sx={{ position: 'relative', flex: 1 }}>
                <StyledTextField
                  label="Enter Part Number"
                  value={partNumber}
                  onChange={handlePartNumberChange}
                  error={validateFields.partNumber.invalid}
                  helperText={validateFields.partNumber.error}
                />
                {showAutocomplete && (
                  <ClickAwayListener onClickAway={handleClickAway}>
                    <Paper
                      ref={autocompleteRef}
                      sx={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        maxHeight: 200,
                        overflow: 'auto',
                        boxShadow: 3,
                        border: '1px solid #ddd',
                        mt: 0.5
                      }}
                    >
                      <List dense>
                        {isAutocompleteLoading ? (
                          <ListItem>
                            <ListItemText primary="Loading..." />
                          </ListItem>
                        ) : suggestions.length > 0 ? (
                          suggestions.map((suggestion: string, index: number) => (
                            <ListItem
                              key={index}
                              component="div"
                              onClick={() => handleSuggestionClick(suggestion)}
                              sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: '#f5f5f5'
                                }
                              }}
                            >
                              <ListItemText primary={suggestion} />
                            </ListItem>
                          ))
                        ) : (
                          <ListItem>
                            <ListItemText primary="No suggestions found" />
                          </ListItem>
                        )}
                      </List>
                    </Paper>
                  </ClickAwayListener>
                )}
              </Box>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#c3183c',
                  color: 'white',
                  borderRadius: '6px',
                  px: 4,
                  '&:hover': {
                    backgroundColor: '#a91532',
                  },
                  height: '40px',
                }}
                type="submit"
              >
                SEARCH
              </Button>
            </Box> 
          </form>
         
        </Box>
      </Box>
       {/* <RecentInventorySearch />  */}
    </DashboardLayout>
    </>
  )
};

export default LeadTime;